import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import InputText from '../Form/Fields/InputText';
import InputDate from '../Form/Fields/InputDate';
import InputQuantity from '../Form/Fields/InputQuantity';
import Textarea from '../Form/Fields/Textarea';
import InputProduct from '../Form/Fields/InputProduct';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faCubes, faChevronDown, faChevronUp, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import Modal from './Modal';
import Form from '../Form/Form';
import axios from 'axios';
import { toast } from 'react-toastify';
import Button from '../Form/Button';
import { API_BASE_URL } from '../../api';
import { useNavigate } from 'react-router-dom';
import { toastCreated } from '../../utils/ToastLink';

import './CreateProductModal.scss';

export default function CreateStockModal({ stock, show, onClose, onSave }) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [expandedLots, setExpandedLots] = useState(new Set());
  const [selectedProductId, setSelectedProductId] = useState(stock?.orderableId ?? null);
  const [negativeStock, setNegativeStock] = useState(null);
  const [outOfStockSold, setOutOfStockSold] = useState(0);

  useEffect(() => {
    setSelectedProductId(stock?.orderableId ?? null);
  }, [stock, show]);

  useEffect(() => {
    if (!selectedProductId) {
      setNegativeStock(null);
      return;
    }
    axios.get(`${API_BASE_URL}/api/v1/products/${selectedProductId}`, { withCredentials: true })
      .then(({ data }) => setNegativeStock(data.totalStock < 0 ? data.totalStock : null))
      .catch(() => setNegativeStock(null));
  }, [selectedProductId]);

  // Units already sold "hors stock" since the last time stock was received for this product —
  // warns the pharmacist not to enter the full quantity they just received if some of it was
  // already sold while out of stock (otherwise the stock ends up overstated).
  useEffect(() => {
    if (!selectedProductId) {
      setOutOfStockSold(0);
      return;
    }
    axios.get(`${API_BASE_URL}/api/v1/stock/orderable/${selectedProductId}`, { withCredentials: true })
      .then(({ data }) => setOutOfStockSold(data.outOfStockSoldSinceLastEntry ?? 0))
      .catch(() => setOutOfStockSold(0));
  }, [selectedProductId]);

  const toggleOptional = (uid) => setExpandedLots(prev => {
    const next = new Set(prev);
    next.has(uid) ? next.delete(uid) : next.add(uid);
    return next;
  });

  const [stockLines, setStockLines] = useState([
    { uid: uuidv4(), batchNumber: '', quantity: 1, expirationDate: '', description: '' },
  ]);

  const addLine = () => setStockLines(prev => [
    ...prev,
    { uid: uuidv4(), batchNumber: '', quantity: 1, expirationDate: '', description: '' },
  ]);

  const removeLine = (uid) => setStockLines(prev => prev.filter(l => l.uid !== uid));

  const onSubmit = (data) => {
    const formatted = stockLines.map(line => ({
      batchNumber:    data[`batchNumber_${line.uid}`],
      quantity:       data[`quantity_${line.uid}`],
      expirationDate: data[`expirationDate_${line.uid}`],
      description:    data[`description_${line.uid}`],
    }));

    if (formatted.some(l => !l.expirationDate)) {
      toast.error('La date de péremption est obligatoire pour chaque lot');
      return;
    }

    setIsLoading(true);
    axios.post(`${API_BASE_URL}/api/v1/stock/add`, {
      orderableId: data.orderableId,
      stockLines: formatted,
    }, { withCredentials: true })
      .then(() => {
        toastCreated(navigate, 'Stock ajouté avec succès !', data.orderableId && `/products/${data.orderableId}`);
        setStockLines([{ uid: uuidv4(), batchNumber: '', quantity: 1, expirationDate: '', description: '' }]);
        onSave?.();
        onClose();
      })
      .catch(err => console.error('Error saving stock:', err))
      .finally(() => setIsLoading(false));
  };

  return (
    <>
      <Modal
        id="createStockModal"
        show={show}
        onClose={onClose}
        title="Nouveau stock"
        icon={faCubes}
        iconBg="#dcfce7"
        iconColor="#16a34a"
        footer={
          <Button
            isLoading={isLoading}
            loadingMessage="Enregistrement..."
            type="submit"
            form="createStockForm"
            className="btn btn-success w-100"
          >
            Alimenter le stock
          </Button>
        }
      >
        <Form id="createStockForm" onSubmit={onSubmit} defaultValues={stock ?? {}}>

          {/* Type + Sélection */}
          <div className="cdm-section">
            <p className="cdm-section__label">Article</p>

            <label className="cdm-field-label">
              Médicament <span className="text-danger">*</span>
            </label>
            <InputProduct
              name="orderableId"
              placeholder={"Rechercher par nom"}
              validationRules={{ required: 'Médicament requis' }}
              handleChange={(opt) => setSelectedProductId(opt?.value ?? null)}
            />

            {negativeStock != null && (
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 8,
                marginTop: 10, padding: '10px 12px',
                background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8,
                fontSize: 12.5, color: '#92400e', lineHeight: 1.4,
              }}>
                <FontAwesomeIcon icon={faTriangleExclamation} style={{ marginTop: 2, flexShrink: 0 }} />
                <span>
                  Ce produit a un stock négatif ({negativeStock}). Ajouter du stock ici l'additionnera
                  au stock existant — pour corriger l'écart plutôt que l'ajouter, utilisez l'Inventaire.
                </span>
              </div>
            )}

            {outOfStockSold > 0 && (
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 8,
                marginTop: 10, padding: '10px 12px',
                background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
                fontSize: 12.5, color: '#b91c1c', lineHeight: 1.4,
              }}>
                <FontAwesomeIcon icon={faTriangleExclamation} style={{ marginTop: 2, flexShrink: 0 }} />
                <span>
                  Attention : {outOfStockSold} unité{outOfStockSold > 1 ? 's' : ''} de ce produit {outOfStockSold > 1 ? 'ont' : 'a'} été
                  vendue{outOfStockSold > 1 ? 's' : ''} hors stock avant cette entrée.
                </span>
              </div>
            )}
          </div>

          {/* Lots */}
          {stockLines.map((line, index) => (
            <div className="cdm-section" key={line.uid}>
              <div className="cdm-section__header">
                <p className="cdm-section__label">Lot de stock</p>
                <div className="d-flex align-items-center gap-2">
                  <span className="cdm-batch-badge">Lot #{index + 1}</span>

                  {stockLines.length > 1 && (
                    <button type="button" className="cdm-remove-btn" onClick={() => removeLine(line.uid)}>
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  )}
                </div>
              </div>

              <div className="cdm-grid-2 mb-2">
                <div>
                  <label className="cdm-field-label">Date d'expiration <span className="text-danger">*</span></label>
                  <InputDate
                    name={`expirationDate_${line.uid}`}
                    mode="month-year"
                    validationRules={{ required: 'Requis' }}
                  />
                </div>
                <div>
                  <label className="cdm-field-label">Quantité <span className="text-danger">*</span></label>
                  <InputQuantity
                    name={`quantity_${line.uid}`}
                    validationRules={{ required: 'Requis', min: { value: 1, message: 'Min 1' } }}
                  />
                </div>
              </div>

              <button
                type="button"
                className="cdm-optional-toggle"
                onClick={() => toggleOptional(line.uid)}
              >
                <FontAwesomeIcon icon={expandedLots.has(line.uid) ? faChevronUp : faChevronDown} />
                {expandedLots.has(line.uid) ? 'Masquer les champs optionnels' : 'Champs optionnels'}
              </button>

              {expandedLots.has(line.uid) && (
                <>
                  <div className="mb-2 mt-2">
                    <label className="cdm-field-label">
                      Numéro de lot
                      <span style={{ fontSize: 11, fontWeight: 400, color: '#9ca3af', marginLeft: 6 }}>optionnel</span>
                    </label>
                    <InputText
                      name={`batchNumber_${line.uid}`}
                      placeholder=""
                    />
                  </div>

                  <div>
                    <label className="cdm-field-label">
                      Description
                      <span style={{ fontSize: 11, fontWeight: 400, color: '#9ca3af', marginLeft: 6 }}>optionnel</span>
                    </label>
                    <Textarea
                      name={`description_${line.uid}`}
                      placeholder=""
                    />
                  </div>
                </>
              )}
            </div>
          ))}

          <button type="button" className="cdm-add-batch" onClick={addLine}>
            <FontAwesomeIcon icon={faPlus} />
            Ajouter un lot
          </button>

        </Form>
      </Modal>
    </>
  );
}
